import { createSlice } from '@reduxjs/toolkit';
import AdminApi from "../../../api/AdminApi";
import {showNotification} from "./notification";
import {IData} from "../../../constants/interfaces";

type State = {
	data: Array<Object>,
	sort: string,
	order: string,
	page: number,
	searchTerm: string,
	isLoading: boolean,
	isUpdating: boolean,
	troubleLoading: boolean,
	error: Object | null,
	timestamp: number,
	paging: {
		total: number | null,
		pages: number | null
	},
	isDirty: boolean
}

const initialState = {
	data: [],
	sort: 'description',
	order: 'asc',
	page: 1,
	searchTerm: '',
	isLoading: false,
	isUpdating: false,
	troubleLoading : false,
	error: null,
	timestamp: 0,
	paging: {
		total: 0,
		pages: 0
	},
	isDirty: false
}

const dashboards = createSlice({
	name: 'dashboards',
	initialState,
	reducers: {
		fetchDashboardsStart: (state: State) => {
			state.isLoading = true;
			state.error = null;
			state.isDirty = false;
		},
		fetchDashboardsSuccess: (state: State, action) => {
			if (action.payload && action.payload.data && Array.isArray(action.payload.data)) {
				state.data = action.payload.data.map((item: any) => {
					return {
						id: item.id,
						description: item.description,
						message: item.message,
						umbrella_id: item.umbrella_id,
						grid_type_id: item.grid_type_id,
						grid_type: item.grid_type,
						grid_config: item.grid_config,
						createdBy: item.createdBy,
						updatedBy: item.updatedBy,
						timestamp: item.timestamp
					}
				});
			}
		
			state.paging = action.payload && action.payload.paging ? action.payload.paging : null;

			state.troubleLoading = false;
			state.isLoading = false;
			state.error = false;
			state.timestamp = Date.now();
		},
		fetchDashboardsTroubleLoading: (state : State, action) => {
			state.isLoading = false;
			state.troubleLoading = true;
		},
		fetchDashboardsFailed: (state: State, action) => {
			state.isLoading = false;
			state.error = action.payload
		},
		saveDashboardStart: (state: State) => {
			state.error = null;
			state.isUpdating = true;
		},
		saveDashboardSuccess: (state: State, action) => {
			state.error = null;
			state.isUpdating = false;
			state.isLoading = false;
			state.isDirty = true;
		},
		saveDashboardFailed: (state: State, action) => {
			state.error = action.payload;
			state.isUpdating = false;
		},
		deleteDashboardStart: (state: State) => {
			state.error = null;
			state.isUpdating = true;
		},
		deleteDashboardSuccess: (state: State, action) => {
			state.error = null;
			state.isUpdating = false;
			state.isDirty = true;
			state.isLoading = false;
		},
		deleteDashboardFailed: (state: State, action) => {
			state.error = action.payload;
			state.isUpdating = false;
		},
		updateDashboardStart: (state: State) => {
			state.error = null;
			state.isUpdating = true;
		},
		updateDashboardSuccess: (state: State, action) => {
			state.error = null;
			state.isUpdating = false;
			state.isLoading = false;
			state.isDirty = true;
		},
		updateDashboardFailed: (state: State, action) => {
			state.error = action.payload;
			state.isUpdating = false;
		},
		setFetchParamsStart: (state: State, action) => {
			state.sort = action.payload.sort;
			state.order = action.payload.order;
			if (action.payload.page) {
				state.page = action.payload.page;
			}

			if (action.payload.searchTerm || action.payload.searchTerm === "") {
				state.searchTerm = action.payload.searchTerm;
			}
		},
	}
});

// Actions
export const {
	fetchDashboardsStart,
	fetchDashboardsSuccess,
	fetchDashboardsFailed,
	fetchDashboardsTroubleLoading,
	saveDashboardStart,
	saveDashboardSuccess,
	saveDashboardFailed,
	deleteDashboardStart,
	deleteDashboardSuccess,
	deleteDashboardFailed,
	updateDashboardStart,
	updateDashboardSuccess,
	updateDashboardFailed,
	setFetchParamsStart
} = dashboards.actions;

export default dashboards.reducer;

// Thunk
export const fetchDashboards = (params?: {sort: string, order: string, searchTerm?: string, page?: number}, limit?: number) => async (dispatch: Function, getState: Function) => {
	try {
		dispatch(fetchDashboardsStart());
		const state = getState().dashboards;
		const api = new AdminApi();
		let result;
		if (limit) {
			result = await api.getDashboards(state.sort, state.order, state.searchTerm, state.page, limit)
		} else {
			result = params
				? await api.getDashboards(params.sort, params.order, params.searchTerm, params.page)
				: await api.getDashboards(state.sort, state.order, state.searchTerm, state.page);
		}

		if (result === "trouble loading") {
			dispatch(fetchDashboardsTroubleLoading(result))
		} else {
			dispatch(fetchDashboardsSuccess(result));
		}
	} catch (err: any) {
		dispatch(fetchDashboardsFailed(err));
		dispatch(showNotification('error', err.message));
	}
}

export const setFetchParams = (params: { sort: string, order: string, page?: number, searchTerm?: string }, refetch: boolean = true) => async (dispatch: Function) => {
	dispatch(setFetchParamsStart(params));

	if (refetch){
		dispatch(fetchDashboards());
	}
}

export const saveDashboard = (data: IData) => async (dispatch: Function) => {
	try {
		dispatch(saveDashboardStart());
		const api = new AdminApi();
		const result = await api.saveDashboard(data);
		dispatch(saveDashboardSuccess(result));
		dispatch(showNotification('success', 'Your new dashboard has been successfully saved.'));
		setTimeout(() => {
			window.location.href = '/admin';
		}, 1000);
	} catch (err: any) {
		dispatch(saveDashboardFailed(err));
		dispatch(showNotification('error', err.message));
	}
}

export const deleteDashboard = (id: string | number) => async (dispatch: Function) => {
	try {
		dispatch(deleteDashboardStart());
		const api = new AdminApi();
		const result = await api.deleteDashboard(id);
		dispatch(deleteDashboardSuccess(result));
		dispatch(showNotification('success', 'Dashboard deleted successfully.'));
	} catch (err: any) {
		dispatch(deleteDashboardFailed(err));
		dispatch(showNotification('error', err.message));
	}
};

export const updateDashboard = (id: string | number, data: IData) => async (dispatch: Function) => {
	try {
		dispatch(updateDashboardStart());
		const api = new AdminApi();
		const result = await api.updateDashboard(id, data);
		dispatch(updateDashboardSuccess(result));
		dispatch(showNotification('success', 'Your dashboard has been successfully updated.'));
		setTimeout(() => {
			window.location.href = '/admin';
		}, 1000);
	} catch (err: any) {
		dispatch(updateDashboardFailed(err));
		dispatch(showNotification('error', err.message));
	}
}
